import { usePage } from '@inertiajs/react'
import { formatDate as formatDateBase } from './dates'
import { type TranslationKey, type TranslationParams, translate } from './i18n'

// Active-locale i18n + formatting context (architecture §6.11). Reads the i18n
// context the server shares on every surface and exposes a typed `t()` plus
// locale-aware currency/date formatters (Intl) that respect the org settings.
export type I18nContext = {
  locale: string
  available_locales: string[]
  unit_system: string
  currency: string
  time_zone: string
}

const FALLBACK: I18nContext = {
  locale: 'en',
  available_locales: ['en'],
  unit_system: 'metric',
  currency: 'USD',
  time_zone: 'UTC',
}

export function useI18n() {
  // `usePage` is always present in the app. Some unit tests mock @inertiajs/react
  // without it, and Vitest's mock *throws on access* of the missing export — so
  // the call is wrapped to fall back to the default i18n context. The hook is
  // still invoked once, unconditionally, on every render (no early return / no
  // branching around it), so Rules of Hooks hold; biome's check is conservative.
  let props: Record<string, unknown> = {}
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: invoked once per render; only the throwing test-mock access is guarded
    props = usePage().props
  } catch {
    props = {}
  }
  const ctx = (props.i18n as I18nContext | undefined) ?? FALLBACK

  return {
    ...ctx,
    t: (key: TranslationKey, params?: TranslationParams) => translate(ctx.locale, key, params),
    formatCurrency: (amount: number) =>
      new Intl.NumberFormat(ctx.locale, { style: 'currency', currency: ctx.currency }).format(
        amount,
      ),
    // US long form — "June 7, 2026" — for calendar dates (ETA/ETD/valid-until/…).
    // Styling lives in `lib/dates.ts` so the whole app restyles from one place.
    formatDate: (date: Date | string) => formatDateBase(date, ctx.locale),
    // Compact date + time for chat timestamps ("Jun 24, 2:32 PM"), in the org's
    // locale + time zone. `full` adds the year + weekday for an unambiguous tooltip.
    formatDateTime: (date: Date | string, full = false) =>
      new Intl.DateTimeFormat(
        ctx.locale,
        full
          ? { timeZone: ctx.time_zone, dateStyle: 'full', timeStyle: 'short' }
          : {
              timeZone: ctx.time_zone,
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            },
      ).format(new Date(date)),
  }
}
